// PLAN-cockpit-v2 Phase 0-C: 共通 Toast (成功 / 失敗 / undo)
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'

type ToastKind = 'success' | 'error' | 'info'

interface ToastItem {
  id: string
  kind: ToastKind
  message: string
  undo?: () => void
  expiresAt: number
}

interface ToastCtx {
  show: (msg: string, opts?: { kind?: ToastKind; durationMs?: number; undo?: () => void }) => void
}

const Ctx = createContext<ToastCtx | null>(null)

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([])

  const show = useCallback<ToastCtx['show']>((msg, opts) => {
    const id = Math.random().toString(36).slice(2)
    const duration = opts?.durationMs ?? (opts?.undo ? 5000 : 3000)
    const item: ToastItem = {
      id,
      kind: opts?.kind ?? 'info',
      message: msg,
      undo: opts?.undo,
      expiresAt: Date.now() + duration,
    }
    setItems((prev) => [...prev, item])
  }, [])

  // 期限切れ削除
  useEffect(() => {
    if (items.length === 0) return
    const t = setInterval(() => {
      const now = Date.now()
      setItems((prev) => prev.filter((i) => i.expiresAt > now))
    }, 250)
    return () => clearInterval(t)
  }, [items.length])

  const dismiss = useCallback((id: string) => {
    setItems((prev) => prev.filter((i) => i.id !== id))
  }, [])

  const value = useMemo(() => ({ show }), [show])

  return (
    <Ctx.Provider value={value}>
      {children}
      <div
        role="region"
        aria-live="polite"
        aria-label="Notifications"
        className="fixed top-4 right-4 z-50 flex flex-col gap-2 pointer-events-none"
      >
        {items.map((it) => (
          <div
            key={it.id}
            className={`
              pointer-events-auto px-4 py-3 rounded-md shadow-overlay text-sm min-w-[280px]
              transition border
              ${it.kind === 'success' ? 'bg-bg-surface border-state-success text-state-success' : ''}
              ${it.kind === 'error' ? 'bg-bg-surface border-state-error text-state-error' : ''}
              ${it.kind === 'info' ? 'bg-bg-surface border-border-strong text-text-base' : ''}
            `}
          >
            <div className="flex items-center justify-between gap-3">
              <span>{it.message}</span>
              <div className="flex items-center gap-2">
                {it.undo && (
                  <button
                    type="button"
                    className="text-xs underline hover:text-accent-primary"
                    onClick={() => {
                      it.undo?.()
                      dismiss(it.id)
                    }}
                  >
                    Undo
                  </button>
                )}
                <button
                  type="button"
                  aria-label="Dismiss"
                  className="text-xs text-text-subtle hover:text-text-base"
                  onClick={() => dismiss(it.id)}
                >
                  ✕
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </Ctx.Provider>
  )
}

export function useToast(): ToastCtx {
  const v = useContext(Ctx)
  if (!v) throw new Error('useToast must be used within ToastProvider')
  return v
}
